/**
 * Admin Chat Room — wraps the shared ChatRoom with full admin moderation controls.
 */
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ChatRoom } from '../shared/ChatRoom';
import { useChatStore } from '../../store/chatStore';

export const AdminChatRoom: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const {
    activeConversation,
    conversationMessages,
    conversationMessagesLoading,
    loadConversation,
    loadConversationMessages,
    sendConversationMessage,
    blockConversation,
    unblockConversation,
    removeMessage,
    clearConversation,
  } = useChatStore();

  useEffect(() => {
    if (!conversationId) return;
    loadConversation(conversationId);
    loadConversationMessages(conversationId);
    return () => clearConversation();
  }, [conversationId, loadConversation, loadConversationMessages, clearConversation]);

  const handleSend = (content: string) => {
    if (!conversationId) return;
    sendConversationMessage(conversationId, content);
  };

  const handleBlock = async (targetId: string, reason: string) => {
    if (!conversationId) return;
    if (reason === '__unblock__') {
      await unblockConversation(conversationId);
    } else {
      await blockConversation(conversationId, reason);
    }
  };

  const handleRemoveMessage = async (messageId: string) => {
    if (!conversationId) return;
    await removeMessage(conversationId, messageId);
  };

  if (conversationMessagesLoading && conversationMessages.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#636E72' }}>
        Loading chat...
      </div>
    );
  }

  return (
    <ChatRoom
      conversation={activeConversation}
      messages={conversationMessages}
      onSend={handleSend}
      onBlockUser={handleBlock}
      onRemoveMessage={handleRemoveMessage}
    />
  );
};
